/**
 * SG Incorporation E-Filing — editable bizfile-shaped model.
 *
 * The setup-entity wizard stores an {@link SgIncorporationForm}. Before the actual
 * bizfile submission the staff user reviews/edits a *bizfile-oriented* model whose
 * shape mirrors `test_incorporation.html` (the `/bizfile/incorporation/create`
 * structured input). This module:
 *   1. defines that editable model,
 *   2. seeds it from the stored incorporation data ({@link seedEfilingModel}),
 *   3. serializes it back to the create-payload ({@link buildBizfileCreatePayload}).
 *
 * Nothing here performs the actual Singpass submission — that stays with the
 * server-side bizfile flow.
 */
import type {
  SgIncorporationForm,
  SgNominatorInfo,
} from '@/features/setupEntity/components/sg/types/sgIncorporation.types';
import {
  createEmptySgAddress,
  normalizeSgAddress,
  type SgAddress,
} from '@/features/setupEntity/components/sg/utils/sgLocalAddress';

/* ────────────────────────────────────────────────────────────────────────
 * Model types (bizfile-oriented, editable in the wizard)
 * ──────────────────────────────────────────────────────────────────────── */

/** Entity information step (BR02A). */
export interface EfEntityInfo {
  /** Reserved-name transaction/unique id (from the reserved company). */
  uniqueId: string;
  companyName: string;
  finYearEnd: string;
  /** 12M (12 months) | 52W (52 weeks accounting period). */
  finYearPeriod: '12M' | '52W';
  registeredAddress: SgAddress;
  /** 3TO5 (3~5h) | OVER5 (5h+). */
  operatingHourType: '3TO5' | 'OVER5' | string;
  /** number of working hours when 3TO5. */
  operatingHours: string;
  email: string;
}

/** A bizfile position holder (individual or corporate). */
export interface EfPositionHolder {
  /** '1' individual | '2' corporate. */
  category: '1' | '2';
  /** LC_DIR / LC_SEC / LC_AUD / LC_SH / LC_MGR / LC_CEO … */
  positions: string[];
  /** registrationIdNo (individual NRIC/FIN/PP, corporate UEN). */
  regNo: string;
  idType: string;
  name: string;
  email: string;
  mobileCountryCode: string;
  mobileAreaCode: string;
  mobileNo: string;
  areaCodeNotApplicable: boolean;
  nationality: string;
  birthDate: string;
  /** individual contact address. (corporate 로컬 아님이면 foreign address 로도 사용) */
  contactAddress: SgAddress;
  /** individual residential address (enrich fallback). */
  residentialAddress: SgAddress;
  /** 법인(category '2') — 로컬(SG) 등록 여부. 기본 YES. */
  registeredLocally?: 'YES' | 'NO';
  /** 법인 + 로컬 아님 — 설립 국가(B15 ISO2). */
  country?: string;
  /** 법인 + 로컬 아님 — 외국 사업자등록번호(FBRN). */
  fbrn?: string;
  /** origin in the setup-entity form (for doc linking / labels; not sent). */
  source?: EfSourceRef;
}

export interface EfSourceRef {
  kind: 'director' | 'secretary' | 'shareholder' | 'other';
  index: number;
}

/** Nominee designation → nominator. */
export interface EfNominator {
  nomineeKind: 'director' | 'shareholder';
  /** index into positionHolders of the nominee. */
  nomineeIndex: number;
  nominatorCategory: 'individual' | 'corporate';
  addInfo: string;
  email: string;
  contactNo: string;
  countryCode: string;
  areaCode: string;
  noArea: boolean;
  // individual
  name: string;
  nameAlias: string;
  idType: string;
  idNo: string;
  nationality: string;
  birthDate: string;
  addrType: 'F' | 'S';
  address: SgAddress;
  // corporate
  registeredLocally: 'YES' | 'NO';
  country: string;
  uen: string;
  fbrn: string;
  entityName: string;
  legalForm: string;
  statute: string;
  register: string;
}

/** A supporting document reference (already uploaded, or a fresh file to upload). */
export interface EfDocRef {
  documentFileName: string;
  /** already-uploaded reference (L_INCORPORATIONS_FILES fileCd). */
  fileCd?: string | null;
  documentId?: string | null;
  type?: string;
  /** freshly attached in the E-Filing screen — base64 body, uploaded server-side at submit. */
  contentBase64?: string | null;
  /** byte size of a freshly attached file. */
  size?: number;
  /** when this fresh file replaces a previously-uploaded one, its fileCd (server may drop the old). */
  replacesFileCd?: string | null;
}

/** RORC controller (individual or corporate). */
export interface EfController {
  kind: 'IND' | 'COM';
  email: string;
  countryCode: string;
  areaCode: string;
  contactNo: string;
  noArea: boolean;
  confirmationFlag: boolean;
  documents: EfDocRef[];
  // individual
  name: string;
  nameAlias: string;
  idType: string;
  idNo: string;
  nationality: string;
  birthDate: string;
  addrType: 'F' | 'S';
  address: SgAddress;
  // corporate
  registeredLocally: 'YES' | 'NO';
  country: string;
  legalForm: string;
  statute: string;
  uen: string;
  fbrn: string;
  entityName: string;
  registrar: string;
}

export interface EfShareSubClass {
  subclassName: string;
  shareAllotted: string;
}

export interface EfShareClass {
  shareType: string;
  issuedCapAmount: string;
  paidupCapAmount: string;
  shareAllotted: string;
  subClasses: EfShareSubClass[];
}

export interface EfShareCurrency {
  currency: string;
  payableMethod: string;
  allotmentMethod: string;
  allotmentMethodDesc: string;
  shareClasses: EfShareClass[];
  documents: EfDocRef[];
}

export interface EfAllotLine {
  shareHolderIndex: number;
  currency: string;
  shareType: string;
  paidupCapAmount: string;
  shareAllotted: string;
  heldInTrust: boolean;
  provideNameOfTrust: boolean;
  nameOfTrust: string;
  subClasses: EfShareSubClass[];
}

export interface EfGroupLine {
  currency: string;
  shareType: string;
  paidupCapAmount: string;
  shareAllotted: string;
  heldInTrust: boolean;
  provideNameOfTrust: boolean;
  nameOfTrust: string;
  subClasses: EfShareSubClass[];
}

export interface EfShareGroup {
  groupName: string;
  shareHolderIndices: number[];
  lines: EfGroupLine[];
}

export interface EfConstitution {
  type: 'MODEL' | 'CUSTM';
  modelType: 'AT' | 'TO';
  documents: EfDocRef[];
}

export interface EfRorc {
  exempt: boolean;
  identify: boolean;
  exemptCategories: string[];
}

export interface EfNomineeExempt {
  exempted: boolean;
  categories: string[];
}

/** The full editable E-Filing model. */
export interface IncorporationEfilingModel {
  entityInfo: EfEntityInfo;
  positionHolders: EfPositionHolder[];
  nomineeExempt: EfNomineeExempt;
  nominators: EfNominator[];
  rorc: EfRorc;
  controllers: EfController[];
  currencies: EfShareCurrency[];
  allotLines: EfAllotLine[];
  groups: EfShareGroup[];
  constitution: EfConstitution;
}

/* ────────────────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────────────────── */

function s(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function addr(a: SgAddress | string | null | undefined): SgAddress {
  return normalizeSgAddress(a as SgAddress);
}

/** SgAddress → bizfile address object (matches test_incorporation.html). */
function toBizAddress(a: SgAddress): Record<string, unknown> {
  const foreign = a.addressTypeCd === 'B07002';
  if (foreign) {
    return {
      addressType: 'F',
      foreignAddress1: s(a.foreignAddressLine1),
      foreignAddress2: s(a.foreignAddressLine2),
    };
  }
  return {
    addressType: 'S',
    postalCode: s(a.postalCode),
    blkhseNo: s(a.blockNo),
    streetName: s(a.streetName),
    buildingName: s(a.buildingName),
    levelNo: s(a.level),
    unitNo: s(a.unit),
    haveLevelUnit: a.haveLevelUnit !== false,
  };
}

/** Nominator address → the flat foreign/local fields the create-body expects. */
function nominatorAddressFields(n: EfNominator): Record<string, unknown> {
  const a = n.address;
  if (n.addrType === 'S' && a.addressTypeCd !== 'B07002') {
    return {
      addrType: 'S',
      postalCode: s(a.postalCode),
      blkhseNo: s(a.blockNo),
      streetName: s(a.streetName),
      buildingName: s(a.buildingName),
      levelNo: s(a.level),
      unitNo: s(a.unit),
      noLU: a.haveLevelUnit === false,
    };
  }
  return {
    addrType: 'F',
    foreignAddr1: s(a.foreignAddressLine1),
    foreignAddr2: s(a.foreignAddressLine2),
  };
}

/* ────────────────────────────────────────────────────────────────────────
 * empty factory
 * ──────────────────────────────────────────────────────────────────────── */

export function createEmptyNominator(
  nomineeKind: 'director' | 'shareholder' = 'director',
  nomineeIndex = -1,
): EfNominator {
  return {
    nomineeKind,
    nomineeIndex,
    nominatorCategory: 'individual',
    addInfo: '',
    email: '',
    contactNo: '',
    countryCode: '65',
    areaCode: '',
    noArea: true,
    name: '',
    nameAlias: '',
    idType: 'PP',
    idNo: '',
    nationality: '',
    birthDate: '',
    addrType: 'F',
    address: { ...createEmptySgAddress(), addressTypeCd: 'B07002' },
    registeredLocally: 'YES',
    country: 'SG',
    uen: '',
    fbrn: '',
    entityName: '',
    legalForm: '',
    statute: '',
    register: '',
  };
}

export function createEmptyController(kind: 'IND' | 'COM' = 'IND'): EfController {
  return {
    kind,
    email: '',
    countryCode: '65',
    areaCode: '',
    contactNo: '',
    noArea: true,
    confirmationFlag: true,
    documents: [],
    name: '',
    nameAlias: '',
    idType: 'PP',
    idNo: '',
    nationality: '',
    birthDate: '',
    addrType: 'F',
    address: { ...createEmptySgAddress(), addressTypeCd: 'B07002' },
    registeredLocally: 'YES',
    country: 'SG',
    legalForm: '',
    statute: '',
    uen: '',
    fbrn: '',
    entityName: '',
    registrar: '',
  };
}

export function createEmptyModel(): IncorporationEfilingModel {
  return {
    entityInfo: {
      uniqueId: '',
      companyName: '',
      finYearEnd: '',
      finYearPeriod: '12M',
      registeredAddress: createEmptySgAddress(),
      operatingHourType: 'OVER5',
      operatingHours: '',
      email: '',
    },
    positionHolders: [],
    nomineeExempt: { exempted: false, categories: [] },
    nominators: [],
    rorc: { exempt: false, identify: true, exemptCategories: [] },
    controllers: [],
    currencies: [],
    allotLines: [],
    groups: [],
    constitution: { type: 'MODEL', modelType: 'AT', documents: [] },
  };
}

/* ────────────────────────────────────────────────────────────────────────
 * seed: SgIncorporationForm → model
 * ──────────────────────────────────────────────────────────────────────── */

function nominatorFromForm(
  src: SgNominatorInfo | null | undefined,
  nomineeKind: 'director' | 'shareholder',
  nomineeIndex: number,
): EfNominator {
  const base = createEmptyNominator(nomineeKind, nomineeIndex);
  if (!src) return base;
  const a = addr(src.address);
  return {
    ...base,
    nominatorCategory: src.category === 'corporate' ? 'corporate' : 'individual',
    addInfo: s(src.additionalInfo),
    email: s(src.email),
    contactNo: s(src.mobileNo),
    countryCode: s(src.mobileCountryCode) || '65',
    areaCode: s(src.mobileAreaCode),
    noArea: src.areaCodeNotApplicable !== false,
    name: s(src.name),
    nameAlias: s(src.nameAlias),
    idType: s(src.idType) || 'PP',
    idNo: s(src.identificationNo),
    nationality: s(src.nationality),
    birthDate: s(src.birthDate),
    addrType: a.addressTypeCd === 'B07002' ? 'F' : 'S',
    address: a,
    registeredLocally: src.registeredLocally === 'NO' ? 'NO' : 'YES',
    country: s(src.country) || 'SG',
    uen: s(src.uen),
    fbrn: s(src.fbrn),
    entityName: s(src.corpName),
    legalForm: s(src.legalForm),
    statute: s(src.statute),
    register: s(src.registerName),
  };
}

/** Seed the editable model from the stored SG incorporation form. */
export function seedEfilingModel(
  form: SgIncorporationForm,
  meta?: { companyName?: string; uniqueId?: string },
): IncorporationEfilingModel {
  const model = createEmptyModel();
  const company = form.company;

  // ── Entity information ──
  model.entityInfo.companyName = s(meta?.companyName) || s(company?.entityName);
  model.entityInfo.uniqueId = s(meta?.uniqueId);
  model.entityInfo.finYearEnd = s(company?.entityProposedFye);
  model.entityInfo.registeredAddress = addr(company?.entityRegisteredAddress);
  model.entityInfo.operatingHourType = s(company?.operatingHourType) || 'OVER5';
  model.entityInfo.operatingHours = s(company?.operatingHours);
  model.entityInfo.email = s(company?.entityEmail);

  // ── Position holders (merged by ID number across every section) ──
  const holders: EfPositionHolder[] = [];
  const dirIndexByHolder = new Map<number, number>(); // director row idx → holder idx
  const shIndexByHolder = new Map<number, number>(); // shareholder row idx → holder idx
  const byRegNo = new Map<string, number>(); // category|regNo → holder idx

  const regKey = (category: string, regNo: string) =>
    `${category}|${String(regNo || '').replace(/\s+/g, '').toUpperCase()}`;

  /** Add a holder, or merge into an existing one with the same category + ID number. */
  function upsertHolder(h: EfPositionHolder): number {
    const rn = String(h.regNo || '').trim();
    const key = regKey(h.category, rn);
    if (rn && byRegNo.has(key)) {
      const idx = byRegNo.get(key) as number;
      const ex = holders[idx];
      for (const p of h.positions) if (p && !ex.positions.includes(p)) ex.positions.push(p);
      // fill blanks from the merged row (keep the first non-empty)
      ex.idType = ex.idType || h.idType;
      ex.name = ex.name || h.name;
      ex.email = ex.email || h.email;
      ex.mobileCountryCode = ex.mobileCountryCode || h.mobileCountryCode;
      ex.mobileAreaCode = ex.mobileAreaCode || h.mobileAreaCode;
      ex.mobileNo = ex.mobileNo || h.mobileNo;
      ex.nationality = ex.nationality || h.nationality;
      ex.birthDate = ex.birthDate || h.birthDate;
      return idx;
    }
    const idx = holders.length;
    holders.push(h);
    if (rn) byRegNo.set(key, idx);
    return idx;
  }

  if (form.includeDirectors !== 'no') {
    (form.directors || []).forEach((d, i) => {
      const contact = addr(d.directorAddress);
      const idx = upsertHolder({
        category: '1',
        positions: d.positions?.length ? [...d.positions] : ['LC_DIR'],
        regNo: s(d.directorIdentificationNo),
        idType: s(d.directorIdType),
        name: s(d.directorName),
        email: s(d.directorEmail),
        mobileCountryCode: s(d.directorMobileCountryCode),
        mobileAreaCode: s(d.directorMobileAreaCode),
        mobileNo: s(d.directorMobileNo),
        areaCodeNotApplicable: d.directorAreaCodeNotApplicable !== false,
        nationality: s(d.directorNationality),
        birthDate: s(d.directorDateOfBirth),
        contactAddress: contact,
        residentialAddress: contact,
        source: { kind: 'director', index: i },
      });
      dirIndexByHolder.set(i, idx);
    });
  }

  if (form.includeSecretary !== 'no' && form.secretary && s(form.secretary.secretaryName)) {
    const sec = form.secretary;
    const contact = addr(sec.secretaryAddress);
    upsertHolder({
      category: '1',
      positions: sec.positions?.length ? [...sec.positions] : ['LC_SEC'],
      regNo: s(sec.secretaryIdentificationNo),
      idType: s(sec.secretaryIdType),
      name: s(sec.secretaryName),
      email: s(sec.secretaryEmail),
      mobileCountryCode: s(sec.secretaryMobileCountryCode),
      mobileAreaCode: s(sec.secretaryMobileAreaCode),
      mobileNo: s(sec.secretaryMobileNo),
      areaCodeNotApplicable: sec.secretaryAreaCodeNotApplicable !== false,
      nationality: s(sec.secretaryNationality),
      birthDate: '',
      contactAddress: contact,
      residentialAddress: contact,
      source: { kind: 'secretary', index: 0 },
    });
  }

  if (form.includeShareholders !== 'no') {
    (form.shareholders || []).forEach((sh, i) => {
      const isCorp = sh.shareholderType === 'corporate';
      const contact = addr(sh.address);
      const idx = upsertHolder({
        category: isCorp ? '2' : '1',
        positions: ['LC_SH'],
        regNo: isCorp ? s(sh.csRegistrationNo) || s(sh.identificationNo) : s(sh.identificationNo),
        idType: isCorp ? '' : s(sh.idType),
        name: isCorp ? s(sh.csCompanyName) || s(sh.name) : s(sh.name),
        email: isCorp ? s(sh.csEmail) : s(sh.email),
        mobileCountryCode: isCorp ? s(sh.csMobileCountryCode) : s(sh.mobileCountryCode),
        mobileAreaCode: isCorp ? s(sh.csMobileAreaCode) : s(sh.mobileAreaCode),
        mobileNo: isCorp ? s(sh.csMobileNo) : s(sh.mobileNo),
        areaCodeNotApplicable: true,
        nationality: s(sh.nationality),
        birthDate: s(sh.dateOfBirth),
        contactAddress: contact,
        residentialAddress: contact,
        source: { kind: 'shareholder', index: i },
      });
      shIndexByHolder.set(i, idx);
    });
  }

  // Other position holders (auditor / CEO / MD). NOMINEE is a flag, not a bizfile position.
  (form.otherPositionHolders || []).forEach((o, i) => {
    if (o.sourceRef) return; // already represented by its source row
    const positions = (o.positions || []).filter((p) => p && p !== 'NOMINEE');
    if (!positions.length) return;
    const a = addr(o.address);
    const isCorp = o.holderType === 'corporate';
    upsertHolder({
      category: isCorp ? '2' : '1',
      positions,
      regNo: s(o.identificationNo),
      idType: s(o.idType),
      name: s(o.name),
      email: s(o.email),
      mobileCountryCode: s(o.mobileCountryCode),
      mobileAreaCode: s(o.mobileAreaCode),
      mobileNo: s(o.mobileNo),
      areaCodeNotApplicable: o.areaCodeNotApplicable !== false,
      nationality: s(o.nationality),
      birthDate: '',
      // 법인: 로컬(SG) 등록 여부 + 외국이면 설립국가/FBRN
      ...(isCorp
        ? {
            registeredLocally: o.registeredLocally === 'NO' ? 'NO' : 'YES',
            country: s(o.country),
            fbrn: s(o.fbrn),
          }
        : {}),
      contactAddress: a,
      residentialAddress: a,
      source: { kind: 'other', index: i },
    });
  });

  model.positionHolders = holders;

  // ── Nominees → nominators ──
  const nominators: EfNominator[] = [];
  (form.directors || []).forEach((d, i) => {
    if (d.isNominee) {
      const hIdx = dirIndexByHolder.get(i);
      if (hIdx != null) nominators.push(nominatorFromForm(d.nominator, 'director', hIdx));
    }
  });
  (form.shareholders || []).forEach((sh, i) => {
    if (sh.isNominee) {
      const hIdx = shIndexByHolder.get(i);
      if (hIdx != null) nominators.push(nominatorFromForm(sh.nominator, 'shareholder', hIdx));
    }
  });
  (form.otherPositionHolders || []).forEach((o) => {
    if ((o.positions || []).includes('NOMINEE')) {
      // other-holder nominee — link to itself if it produced a holder
      const hIdx = model.positionHolders.findIndex(
        (h) => h.source?.kind === 'other' && s(h.regNo) === s(o.identificationNo),
      );
      nominators.push(nominatorFromForm(o.nominator, 'director', hIdx));
    }
  });
  model.nominators = nominators;

  // ── Controllers (RORC) ──
  if (form.includeControllers !== 'no') {
    const controllers: EfController[] = [];
    (form.individualControllers || []).forEach((c) => {
      const base = createEmptyController('IND');
      const a = addr(c.controllerResidentialAddress);
      controllers.push({
        ...base,
        email: s(c.controllerEmail),
        countryCode: s(c.controllerContactCountryCode) || '65',
        areaCode: s(c.controllerContactAreaCode),
        contactNo: s(c.controllerContactNo),
        confirmationFlag: c.confirmationFlag !== false,
        documents: (c.supportingDocs || [])
          .filter((d) => d.fileCd)
          .map((d) => ({ documentFileName: '', fileCd: d.fileCd })),
        name: s(c.controllerFullName),
        nameAlias: s(c.controllerAliases),
        idType: s(c.controllerIdType) || 'PP',
        idNo: s(c.controllerIdentificationNo),
        nationality: s(c.controllerNationality),
        birthDate: s(c.controllerDateOfBirth),
        addrType: a.addressTypeCd === 'B07002' ? 'F' : 'S',
        address: a,
      });
    });
    (form.corporateControllers || []).forEach((c) => {
      const base = createEmptyController('COM');
      const a = addr(c.controllerRegisteredOffice);
      const local = a.addressTypeCd !== 'B07002';
      controllers.push({
        ...base,
        email: s(c.controllerEmail),
        countryCode: s(c.controllerContactCountryCode) || '65',
        areaCode: s(c.controllerContactAreaCode),
        contactNo: s(c.controllerContactNo),
        confirmationFlag: c.confirmationFlag !== false,
        documents: (c.supportingDocs || [])
          .filter((d) => d.fileCd)
          .map((d) => ({ documentFileName: '', fileCd: d.fileCd })),
        registeredLocally: local ? 'YES' : 'NO',
        country: 'SG',
        legalForm: s(c.controllerLegalForm),
        statute: s(c.controllerJurisdictionStatute),
        uen: s(c.controllerUen),
        entityName: s(c.controllerCorporateName),
        registrar: s(c.controllerRegisterName),
        address: a,
        addrType: local ? 'S' : 'F',
      });
    });
    model.controllers = controllers;
  }

  // ── Shares ──
  const struct = form.shareStructure;
  if (struct) {
    model.currencies = (struct.currencies || []).map((c) => ({
      currency: s(c.currency) || 'SGD',
      payableMethod: s(c.payableMethod) || 'CASH',
      allotmentMethod: s(c.allotmentMethod),
      allotmentMethodDesc: s(c.allotmentMethodDesc),
      shareClasses: (c.shareClasses || []).map((sc) => ({
        shareType: s(sc.shareType) || 'ORD',
        issuedCapAmount: s(sc.issuedCapAmount),
        paidupCapAmount: s(sc.paidupCapAmount),
        shareAllotted: s(sc.shareAllotted),
        subClasses: (sc.subClasses || []).map((sub) => ({
          subclassName: s(sub.subclassName),
          shareAllotted: s(sub.shareAllotted),
        })),
      })),
      documents: c.allotmentDocFileCd
        ? [{ documentFileName: '', fileCd: c.allotmentDocFileCd }]
        : [],
    }));

    model.groups = (struct.groups || []).map((g) => ({
      groupName: s(g.groupName),
      shareHolderIndices: (g.shareholderIndices || [])
        .map((idx) => shIndexByHolder.get(idx))
        .filter((v): v is number => v != null),
      lines: (g.lines || []).map((l) => ({
        currency: s(l.currency) || 'SGD',
        shareType: s(l.shareType) || 'ORD',
        paidupCapAmount: s(l.shareAllotted),
        shareAllotted: s(l.shareAllotted),
        heldInTrust: false,
        provideNameOfTrust: false,
        nameOfTrust: '',
        subClasses: (l.subClasses || []).map((sub) => ({
          subclassName: s(sub.subclassName),
          shareAllotted: s(sub.shareAllotted),
        })),
      })),
    }));
  }

  // Per-shareholder allotment lines (individual shareholdings).
  const allotLines: EfAllotLine[] = [];
  (form.shareholders || []).forEach((sh, i) => {
    const hIdx = shIndexByHolder.get(i);
    if (hIdx == null) return;
    const alls = sh.allotments || [];
    if (alls.length) {
      // 주주 배정(통화·클래스별) — setup entity allotments 가 소스
      alls.forEach((a) => {
        if (!s(a.noOfShares) && !s(a.shareType)) return;
        allotLines.push({
          shareHolderIndex: hIdx,
          currency: s(a.currency) || model.currencies[0]?.currency || 'SGD',
          shareType: s(a.shareType) || 'ORD',
          paidupCapAmount: s(a.paidUpCapital) || s(a.noOfShares),
          shareAllotted: s(a.noOfShares),
          heldInTrust: !!a.heldInTrust,
          provideNameOfTrust: !!a.provideNameOfTrust,
          nameOfTrust: s(a.nameOfTrust),
          subClasses: (a.subClasses || []).map((sub) => ({
            subclassName: s(sub.subclassName),
            shareAllotted: s(sub.shareAllotted),
          })),
        });
      });
    } else if (s(sh.noOfShares) || s(sh.shareType)) {
      // 레거시 폴백 — allotments 없는 구 데이터의 평면 필드
      allotLines.push({
        shareHolderIndex: hIdx,
        currency: model.currencies[0]?.currency || 'SGD',
        shareType: s(sh.shareType) || 'ORD',
        paidupCapAmount: s(sh.noOfShares),
        shareAllotted: s(sh.noOfShares),
        heldInTrust: !!sh.heldInTrust,
        provideNameOfTrust: !!sh.provideNameOfTrust,
        nameOfTrust: s(sh.nameOfTrust),
        subClasses: (sh.subClasses || []).map((sub) => ({
          subclassName: s(sub.subclassName),
          shareAllotted: s(sub.shareAllotted),
        })),
      });
    }
  });
  model.allotLines = allotLines;

  return model;
}

/* ────────────────────────────────────────────────────────────────────────
 * serialize: model → /bizfile/incorporation/create body
 * ──────────────────────────────────────────────────────────────────────── */

function docToSend(d: EfDocRef): Record<string, unknown> {
  // Freshly attached in this screen → send the file body (server uploads it).
  if (d.contentBase64) {
    return {
      filename: s(d.documentFileName),
      content_type: s(d.type) || 'application/pdf',
      content_base64: d.contentBase64,
      // when replacing a previously-uploaded file, name the old one so the server can drop it.
      replaces_file_cd: s(d.replacesFileCd),
    };
  }
  // Already-uploaded file (L_INCORPORATIONS_FILES) → reference by fileCd; the
  // server resolves the bytes.
  return {
    document_file_name: s(d.documentFileName),
    file_cd: s(d.fileCd),
    document_id: s(d.documentId),
    type: s(d.type) || 'application/pdf',
  };
}

function positionHolderToSend(h: EfPositionHolder): Record<string, unknown> {
  if (h.category === '1') {
    return {
      category: '1',
      positions: h.positions,
      reg_no: s(h.regNo),
      id_type: s(h.idType),
      name: s(h.name),
      email: s(h.email),
      mobile_no: s(h.mobileNo),
      mobile_country_code: s(h.mobileCountryCode),
      mobile_area_code: s(h.mobileAreaCode),
      area_code_not_applicable: !!h.areaCodeNotApplicable,
      contact_address: toBizAddress(h.contactAddress),
      profile_id: '',
      nationality: s(h.nationality),
      birth_date: s(h.birthDate),
      resi_address_source: 'A',
      residential_address:
        s(h.residentialAddress.postalCode) || s(h.residentialAddress.foreignAddressLine1)
          ? toBizAddress(h.residentialAddress)
          : {},
      corporate_address: {},
      ui_suffix: '',
      registered_locally: true,
    };
  }
  const localCorp = (h.registeredLocally ?? 'YES') !== 'NO';
  return {
    category: '2',
    positions: h.positions,
    reg_no: localCorp ? s(h.regNo) : '',
    id_type: '',
    name: s(h.name),
    profile_id: '',
    ui_suffix: '',
    // 로컬 아님(외국 법인): 설립국가(B15 ISO2) + FBRN + 외국 주소
    registered_locally: localCorp,
    incorp_country: localCorp ? '' : s(h.country),
    fbrn: localCorp ? '' : s(h.fbrn),
    corporate_address: localCorp ? {} : toBizAddress(h.contactAddress),
    nationality: '',
    birth_date: '',
    email: s(h.email),
    mobile_no: s(h.mobileNo),
    mobile_country_code: s(h.mobileCountryCode),
    mobile_area_code: s(h.mobileAreaCode),
    area_code_not_applicable: !!h.areaCodeNotApplicable,
    contact_address: {},
    resi_address_source: 'A',
    residential_address: {},
  };
}

function nominatorToSend(n: EfNominator): Record<string, unknown> {
  const common: Record<string, unknown> = {
    nominee_kind: n.nomineeKind,
    nominee_index: n.nomineeIndex,
    category: n.nominatorCategory === 'corporate' ? '2' : '1',
    addInfo: s(n.addInfo),
    email: s(n.email),
    contactNo: s(n.contactNo),
    countryCode: s(n.countryCode),
    areaCode: s(n.areaCode),
    noArea: !!n.noArea,
  };
  if (n.nominatorCategory === 'individual') {
    return {
      ...common,
      name: s(n.name),
      nameAlias: s(n.nameAlias),
      idType: s(n.idType),
      idNo: s(n.idNo),
      nationality: s(n.nationality),
      birthDate: s(n.birthDate),
      ...nominatorAddressFields(n),
    };
  }
  const corp: Record<string, unknown> = {
    ...common,
    registeredLocally: n.registeredLocally,
    country: s(n.country) || 'SG',
    legalForm: s(n.legalForm),
    statute: s(n.statute),
    register: s(n.register),
  };
  if (n.registeredLocally === 'NO') {
    corp.fbrn = s(n.fbrn);
    corp.entityName = s(n.entityName);
    corp.foreignAddr1 = s(n.address.foreignAddressLine1);
    corp.foreignAddr2 = s(n.address.foreignAddressLine2);
  } else {
    corp.uen = s(n.uen);
  }
  return corp;
}

function controllerToSend(c: EfController): Record<string, unknown> {
  const common: Record<string, unknown> = {
    kind: c.kind,
    email: s(c.email),
    countryCode: s(c.countryCode),
    areaCode: s(c.areaCode),
    contactNo: s(c.contactNo),
    noArea: !!c.noArea,
    confirmationFlag: !!c.confirmationFlag,
    documents: (c.documents || []).map(docToSend),
  };
  if (c.kind === 'IND') {
    return {
      ...common,
      name: s(c.name),
      nameAlias: s(c.nameAlias),
      idType: s(c.idType),
      idNo: s(c.idNo),
      nationality: s(c.nationality),
      birthDate: s(c.birthDate),
      addrType: c.addrType,
      foreignAddr1: s(c.address.foreignAddressLine1),
      foreignAddr2: s(c.address.foreignAddressLine2),
      postalCode: s(c.address.postalCode),
      blkhseNo: s(c.address.blockNo),
      streetName: s(c.address.streetName),
      buildingName: s(c.address.buildingName),
      levelNo: s(c.address.level),
      unitNo: s(c.address.unit),
      noLU: c.address.haveLevelUnit === false,
    };
  }
  const corp: Record<string, unknown> = {
    ...common,
    registeredLocally: c.registeredLocally,
    country: s(c.country),
    legalForm: s(c.legalForm),
    statute: s(c.statute),
  };
  if (c.registeredLocally === 'NO') {
    corp.fbrn = s(c.fbrn);
    corp.entityName = s(c.entityName);
    corp.registrar = s(c.registrar);
    corp.foreignAddr1 = s(c.address.foreignAddressLine1);
    corp.foreignAddr2 = s(c.address.foreignAddressLine2);
  } else {
    corp.uen = s(c.uen);
  }
  return corp;
}

function subClassesToSend(subs: EfShareSubClass[]): Record<string, unknown>[] {
  return (subs || [])
    .filter((sub) => s(sub.subclassName))
    .map((sub) => ({ subclass_name: s(sub.subclassName), share_allotted: s(sub.shareAllotted) }));
}

function collectShares(model: IncorporationEfilingModel): Record<string, unknown> {
  // merge same-currency entries (currency=1 → many shareClasses)
  const byCurrency: Record<string, EfShareCurrency> = {};
  for (const c of model.currencies) {
    const key = c.currency || 'SGD';
    if (!byCurrency[key]) {
      byCurrency[key] = {
        currency: key,
        payableMethod: c.payableMethod,
        allotmentMethod: c.allotmentMethod,
        allotmentMethodDesc: c.allotmentMethodDesc,
        shareClasses: [],
        documents: [],
      };
    }
    byCurrency[key].shareClasses.push(...c.shareClasses);
    byCurrency[key].documents.push(...(c.documents || []));
    byCurrency[key].payableMethod = c.payableMethod;
    byCurrency[key].allotmentMethod = c.allotmentMethod;
    byCurrency[key].allotmentMethodDesc = c.allotmentMethodDesc;
  }
  return {
    share_capitals: Object.values(byCurrency).map((c) => ({
      currency: c.currency,
      payable_method: c.payableMethod,
      allotment_method: c.allotmentMethod || '',
      allotment_method_desc: c.allotmentMethodDesc || '',
      share_classes: c.shareClasses.map((sc) => ({
        share_type: sc.shareType,
        issued_cap_amount: s(sc.issuedCapAmount),
        paidup_cap_amount: s(sc.paidupCapAmount),
        share_allotted: s(sc.shareAllotted),
        sub_classes: subClassesToSend(sc.subClasses),
      })),
      documents: (c.documents || []).map(docToSend),
    })),
    share_allotment: {
      lines: model.allotLines.map((l) => ({
        share_holder_index: l.shareHolderIndex,
        currency: l.currency,
        share_type: l.shareType,
        paidup_cap_amount: s(l.paidupCapAmount),
        share_allotted: s(l.shareAllotted),
        held_in_trust: !!l.heldInTrust,
        provide_name_of_trust: !!l.provideNameOfTrust,
        name_of_trust: s(l.nameOfTrust),
        sub_classes: subClassesToSend(l.subClasses),
      })),
      group: model.groups.map((g) => ({
        group_name: s(g.groupName),
        share_holder_indices: g.shareHolderIndices,
        lines: g.lines.map((l) => ({
          currency: l.currency,
          share_type: l.shareType,
          paidup_cap_amount: s(l.paidupCapAmount),
          share_allotted: s(l.shareAllotted),
          held_in_trust: !!l.heldInTrust,
          provide_name_of_trust: !!l.provideNameOfTrust,
          name_of_trust: s(l.nameOfTrust),
          sub_classes: subClassesToSend(l.subClasses),
        })),
      })),
    },
  };
}

function collectConstitution(c: EfConstitution): Record<string, unknown> {
  if (c.type === 'CUSTM') {
    return {
      constitution_type: 'CUSTM',
      model_constitution_type: null,
      documents: (c.documents || []).map(docToSend),
    };
  }
  return { constitution_type: 'MODEL', model_constitution_type: c.modelType, documents: [] };
}

/**
 * Serialize the reviewed model to the `/bizfile/incorporation/create` body.
 * `session_id` is added by the caller/server at actual-submit time.
 */
export function buildBizfileCreatePayload(
  model: IncorporationEfilingModel,
): Record<string, unknown> {
  const info = model.entityInfo;
  const reg = info.registeredAddress;
  return {
    unique_id: s(info.uniqueId),
    fin_year_end: s(info.finYearEnd),
    fin_year_period: info.finYearPeriod,
    postal_code: s(reg.postalCode),
    blkhse_no: s(reg.blockNo),
    street_name: s(reg.streetName),
    building_name: s(reg.buildingName),
    level_no: s(reg.level),
    unit_no: s(reg.unit),
    have_level_unit: reg.haveLevelUnit !== false,
    operating_hour_type: info.operatingHourType,
    operating_hours: info.operatingHourType === '3TO5' ? s(info.operatingHours) : '',
    email: s(info.email),
    position_holders: model.positionHolders.map(positionHolderToSend),
    nominators: model.nominators.map(nominatorToSend),
    controllers: model.controllers.map(controllerToSend),
    rorc_exempt: !!model.rorc.exempt,
    rorc_identify: !!model.rorc.identify,
    rorc_exempt_categories: model.rorc.exemptCategories,
    nominator_exempted: !!model.nomineeExempt.exempted,
    nominator_exempt_categories: model.nomineeExempt.exempted ? model.nomineeExempt.categories : [],
    ...collectShares(model),
    constitution: collectConstitution(model.constitution),
  };
}
